import { Navigate, useLocation } from "react-router";
import { Alert, Button } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UserProvider } from "shared/context/user/user-provider";
import { useAccessibleShops } from "shared/hooks/useAccessibleShops";
import { useLastVisitedShopId } from "shared/hooks/useLastVisitedShopId";
import { useLogout } from "shared/hooks/useLogout";
import { useNotifications } from "shared/hooks/useNotifications";
import { PageLoading } from "shared/components/utils/PageLoading";

import { isLoggedIn } from "legacy/requireAuth";
import { to } from "../to";

const RootRedirect = () => {
  const { search, hash } = useLocation();

  return !isLoggedIn() ? (
    <Navigate
      to={{
        pathname: to.login,
        search,
        hash
      }}
      replace
    />
  ) : (
    <UserProvider>
      <DefaultShopRedirect />
    </UserProvider>
  );
};

const DefaultShopRedirect = () => {
  const {
    data: shops,
    isLoading,
    isFetching,
    isPaused,
    isError,
    isSuccess,
    refetch
  } = useAccessibleShops({ refetchOnMount: "always" });
  const { lastVisitedShopId } = useLastVisitedShopId();
  const { t } = useTranslation();
  const { showNotification } = useNotifications();
  const logout = useLogout();
  const hasLoggedOut = useRef(false);
  const hasNoShops = isSuccess && !isFetching && !isPaused && shops.length === 0;
  const shop = shops?.find(({ shopId }) => shopId === lastVisitedShopId) ?? shops?.[0];

  useEffect(logoutWhenNoShopsAreConfigured, [hasNoShops, logout, showNotification, t]);

  if (isError) {
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => void refetch()}>{t("shopContext.retry")}</Button>}
      >
        {t("global.error.serverUnavailable")}
      </Alert>
    );
  }
  if (isLoading || isFetching || isPaused || !shop) return <PageLoading />;
  return <Navigate to={to.dashboard(shop.shopId)} replace />;

  function logoutWhenNoShopsAreConfigured() {
    if (!hasNoShops || hasLoggedOut.current) return;
    hasLoggedOut.current = true;
    showNotification(t("shopContext.noShops"), "warning");
    logout();
  }
};

export { RootRedirect };
